import { useId, useState, type ReactNode } from 'react'
import {
  AlertCircle,
  AlertTriangle,
  ArrowRight,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  FileX2,
  ListChecks,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utils/cn'
import type { ImportIssueEntry } from './importReview'

export type IssueTone = 'critical' | 'neutral' | 'warning' | 'info'

const TONES: Record<
  IssueTone,
  { icon: typeof AlertCircle; stripe: string; iconClass: string }
> = {
  critical: {
    icon: AlertCircle,
    stripe: 'bg-destructive',
    iconClass: 'text-destructive',
  },
  neutral: {
    icon: FileX2,
    stripe: 'bg-[#8A93A3]',
    iconClass: 'text-[#556374]',
  },
  warning: {
    icon: AlertTriangle,
    stripe: 'bg-amber-500',
    iconClass: 'text-amber-600',
  },
  info: { icon: ListChecks, stripe: 'bg-blue-500', iconClass: 'text-blue-600' },
}

export const GROUP_PAGE_SIZE = 10

interface ImportIssueGroupProps {
  tone: IssueTone
  title: string
  description: string
  count: number
  defaultOpen?: boolean
  action?: ReactNode
  children: ReactNode
}

/** Collapsible section for one severity level of import issues. */
export function ImportIssueGroup({
  tone,
  title,
  description,
  count,
  defaultOpen = false,
  action,
  children,
}: ImportIssueGroupProps) {
  const [open, setOpen] = useState(defaultOpen)
  const contentId = useId()
  const { icon: Icon, stripe, iconClass } = TONES[tone]

  return (
    <section
      aria-label={title}
      className="relative overflow-hidden rounded-lg border border-[#E5E7EB] bg-white"
    >
      <span
        aria-hidden="true"
        className={cn('absolute inset-y-0 left-0 w-[3px]', stripe)}
      />
      <div className="flex items-center gap-2 pr-3">
        <button
          type="button"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={() => setOpen(value => !value)}
          className="flex min-w-0 flex-1 cursor-pointer items-center gap-3 rounded-lg py-3 pl-4 pr-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Icon
            aria-hidden="true"
            className={cn('h-4 w-4 shrink-0', iconClass)}
          />
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="flex items-center gap-2 text-sm font-semibold text-[#1C1C1C]">
              {title}
              <span className="rounded-full border border-[#E5E7EB] bg-[#F8F8F8] px-1.5 text-[11px] font-semibold tabular-nums text-[#556374]">
                {count.toLocaleString()}
              </span>
            </span>
            <span className="text-[13px] text-[#556374]">{description}</span>
          </span>
          <ChevronDown
            aria-hidden="true"
            className={cn(
              'h-4 w-4 shrink-0 text-[#6A7282] transition-transform motion-reduce:transition-none',
              open && 'rotate-180'
            )}
          />
        </button>
        {action}
      </div>
      <div id={contentId} hidden={!open} className="border-t border-[#E5E7EB]">
        {children}
      </div>
    </section>
  )
}

const ROW_PREVIEW_LIMIT = 8

function RowNumbers({ rows }: { rows: number[] }) {
  const [expanded, setExpanded] = useState(false)
  const visible = expanded ? rows : rows.slice(0, ROW_PREVIEW_LIMIT)
  const hidden = rows.length - visible.length
  return (
    <p className="text-xs tabular-nums text-[#6A7282] sm:text-right">
      {rows.length === 1 ? 'Row' : 'Rows'} {visible.join(', ')}
      {hidden > 0 && (
        <>
          {' '}
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="cursor-pointer font-medium text-[#556374] underline underline-offset-2"
          >
            +{hidden} more
          </button>
        </>
      )}
    </p>
  )
}

function ValueChip({
  value,
  variant = 'default',
}: {
  value: string
  variant?: 'default' | 'corrected'
}) {
  return (
    <span
      className={cn(
        'rounded border px-1.5 py-px font-mono text-xs [overflow-wrap:anywhere]',
        variant === 'corrected'
          ? 'border-blue-200 bg-blue-50 text-blue-800'
          : 'border-[#E5E7EB] bg-[#F8F8F8] text-[#1C1C1C]'
      )}
    >
      {value}
    </span>
  )
}

export function IssueEntryRow({
  label,
  children,
  rows,
}: {
  label: string
  children: ReactNode
  rows?: number[]
}) {
  return (
    <li className="grid gap-1 border-b border-[#E5E7EB] py-3 pl-4 pr-3 last:border-b-0 sm:grid-cols-[150px_minmax(0,1fr)_auto] sm:gap-4">
      <p className="text-[13px] font-semibold text-[#1C1C1C] [overflow-wrap:anywhere]">
        {label}
      </p>
      <div className="min-w-0 space-y-1 text-[13px] text-[#1C1C1C]">
        {children}
      </div>
      {rows && <RowNumbers rows={rows} />}
    </li>
  )
}

function IssueEntryContent({ entry }: { entry: ImportIssueEntry }) {
  if (entry.correctedValue != null) {
    return (
      <p className="flex flex-wrap items-center gap-1.5">
        <ValueChip value={entry.value ?? '—'} />
        <ArrowRight
          aria-label="corrected to"
          className="h-3.5 w-3.5 text-[#6A7282]"
        />
        <ValueChip value={entry.correctedValue} variant="corrected" />
      </p>
    )
  }
  const showValue =
    entry.value != null &&
    entry.value !== '' &&
    !entry.message.includes(entry.value)
  return (
    <>
      <p className="flex flex-wrap items-center gap-1.5">
        {showValue && <ValueChip value={entry.value!} />}
        <span>{entry.message}</span>
      </p>
      {entry.hint && <p className="text-xs text-[#556374]">{entry.hint}</p>}
    </>
  )
}

export function GroupPager({
  page,
  pageSize = GROUP_PAGE_SIZE,
  total,
  onPageChange,
}: {
  page: number
  pageSize?: number
  total: number
  onPageChange: (page: number) => void
}) {
  if (total <= pageSize) return null
  const lastPage = Math.ceil(total / pageSize)
  const first = (page - 1) * pageSize + 1
  const last = Math.min(page * pageSize, total)
  return (
    <div className="flex items-center justify-end gap-1 border-t border-[#E5E7EB] px-3 py-2 text-xs text-[#556374]">
      <span className="mr-2 tabular-nums">
        {first}–{last} of {total.toLocaleString()}
      </span>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label="Previous page"
        disabled={page <= 1}
        onClick={() => onPageChange(page - 1)}
      >
        <ChevronLeft aria-hidden="true" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        aria-label="Next page"
        disabled={page >= lastPage}
        onClick={() => onPageChange(page + 1)}
      >
        <ChevronRight aria-hidden="true" />
      </Button>
    </div>
  )
}

/** Client-paginated list of grouped issues. */
export function IssueEntryList({ entries }: { entries: ImportIssueEntry[] }) {
  const [page, setPage] = useState(1)
  const visible = entries.slice(
    (page - 1) * GROUP_PAGE_SIZE,
    page * GROUP_PAGE_SIZE
  )
  return (
    <>
      <ul>
        {visible.map(entry => (
          <IssueEntryRow key={entry.id} label={entry.column} rows={entry.rows}>
            <IssueEntryContent entry={entry} />
          </IssueEntryRow>
        ))}
      </ul>
      <GroupPager page={page} total={entries.length} onPageChange={setPage} />
    </>
  )
}
